import { OnboardingStep } from '@repo/shared/dtos/organization'
import {
  GenerationEnum,
  GenerationEvent,
  GenerationStatusEnum,
} from '@repo/shared/events/generation'
import { generateId } from '@repo/shared/uuid/uuid'

import { AssistantFeedItem } from '@/store/assistant'
import { FakeGenerationEnum } from '@/store/middlewares/types'

export const getStaticAssistantItems = (
  key: GenerationEnum | FakeGenerationEnum,
  payload: { generationId: string },
): GenerationEvent<AssistantFeedItem>[] => {
  if ('generationId' in payload === false || typeof payload.generationId !== 'string')
    throw new Error('Invalid payload: generationId is required')
  const { generationId } = payload

  switch (key) {
    case FakeGenerationEnum.AskForDescription: {
      if ('organizationStep' in payload === false || typeof payload.organizationStep !== 'string')
        throw new Error('Invalid payload: organizationStep is required')
      const { organizationStep } = payload as {
        organizationStep: OnboardingStep
      }
      if (organizationStep === OnboardingStep.COMPLETED)
        return [
          {
            generationId,
            status: GenerationStatusEnum.Streaming,
            payload: {
              id: generateId(),
              transKeyContent: 'wantNewDescription',
              generationId,
              type: 'message',
              sentAt: new Date().getTime(),
            },
          },
          {
            generationId,
            status: GenerationStatusEnum.Finished,
            payload: {
              id: generateId(),
              type: 'cta',
              generationId,
              cta: 'generateDescription',
              sentAt: new Date().getTime(),
            },
          },
        ]
      return []
    }

    case FakeGenerationEnum.AskForUpdatingDescription: {
      if ('description' in payload === false || typeof payload.description !== 'string')
        throw new Error('Invalid payload: description is required')
      const { description } = payload
      return [
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            id: generateId(),
            content: description,
            type: 'message',
            generationId,
            traceUrl: 'traceUrl' in payload ? (payload.traceUrl as string) : undefined,
            sentAt: new Date().getTime(),
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Finished,
          payload: {
            id: generateId(),
            type: 'cta',
            cta: 'applyDescription',
            generationId,
            sentAt: new Date().getTime(),
          },
        },
      ]
    }

    case FakeGenerationEnum.AskForMethodology: {
      if ('organizationStep' in payload === false || typeof payload.organizationStep !== 'string')
        throw new Error('Invalid payload: organizationStep is required')
      const { organizationStep } = payload as {
        organizationStep: OnboardingStep
      }
      if (organizationStep === OnboardingStep.ORGANIZATION)
        return [
          {
            generationId,
            status: GenerationStatusEnum.Streaming,
            payload: {
              transKeyWhileProcessing: 'onboarding_14',
              transKeyAfterProcessing: 'onboarding_15',
              id: generateId(),
              type: 'processingMessage',
              generationId,
              sentAt: new Date().getTime(),
            },
          },
          {
            generationId,
            status: GenerationStatusEnum.Streaming,
            payload: {
              transKeyContent: 'onboarding_7',
              id: generateId(),
              type: 'message',
              generationId,
              sentAt: new Date().getTime(),
            },
          },
          {
            generationId,
            status: GenerationStatusEnum.Finished,
            payload: {
              id: generateId(),
              type: 'cta',
              cta: 'askForMethodology',
              generationId,
              sentAt: new Date().getTime(),
            },
          },
        ]
      else
        return [
          {
            generationId,
            status: GenerationStatusEnum.Streaming,
            payload: {
              id: generateId(),
              transKeyContent: 'wantNewMethodology',
              generationId,
              type: 'message',
              sentAt: new Date().getTime(),
            },
          },
          {
            generationId,
            status: GenerationStatusEnum.Finished,
            payload: {
              id: generateId(),
              type: 'cta',
              generationId,
              cta: 'generateMethodology',
              sentAt: new Date().getTime(),
            },
          },
        ]
    }

    case FakeGenerationEnum.AskForUpdatingMethodology: {
      if ('methodology' in payload === false || typeof payload.methodology !== 'string')
        throw new Error('Invalid payload: methodology is required')
      const { methodology } = payload
      return [
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            generationId,
            id: generateId(),
            content: methodology,
            type: 'message',
            traceUrl: 'traceUrl' in payload ? (payload.traceUrl as string) : undefined,
            sentAt: new Date().getTime(),
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Finished,
          payload: {
            generationId,
            id: generateId(),
            type: 'cta',
            cta: 'applyMethodology',
            sentAt: new Date().getTime(),
          },
        },
      ]
    }

    case FakeGenerationEnum.AskForResources: {
      return [
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            sentAt: new Date().getTime(),
            transKeyWhileProcessing: 'onboarding_16',
            transKeyAfterProcessing: 'onboarding_17',
            id: generateId(),
            type: 'processingMessage',
            generationId,
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            sentAt: new Date().getTime(),
            transKeyContent: 'onboarding_9',
            id: generateId(),
            type: 'message',
            generationId,
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Finished,
          payload: {
            sentAt: new Date().getTime(),
            id: generateId(),
            type: 'cta',
            cta: 'askForResources',
            generationId,
          },
        },
      ]
    }

    case FakeGenerationEnum.AskForMembers: {
      return [
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            sentAt: new Date().getTime(),
            transKeyContent: 'onboarding_10',
            id: generateId(),
            type: 'message',
            generationId,
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            sentAt: new Date().getTime(),
            transKeyContent: 'onboarding_11',
            id: generateId(),
            type: 'message',
            generationId,
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            sentAt: new Date().getTime(),
            transKeyContent: 'onboarding_12',
            id: generateId(),
            type: 'message',
            generationId,
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Finished,
          payload: {
            sentAt: new Date().getTime(),
            id: generateId(),
            type: 'cta',
            cta: 'askForMembers',
            generationId,
          },
        },
      ]
    }

    case FakeGenerationEnum.AskForCompleteOnboarding: {
      return [
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            sentAt: new Date().getTime(),
            transKeyContent: 'onboarding_13',
            id: generateId(),
            type: 'message',
            generationId,
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Finished,
          payload: {
            sentAt: new Date().getTime(),
            id: generateId(),
            type: 'cta',
            cta: 'askForComplete',
            generationId,
          },
        },
      ]
    }

    case GenerationEnum.GenerateCaseworkerDashboard: {
      return [
        {
          generationId,
          status: GenerationStatusEnum.Streaming,
          payload: {
            sentAt: new Date().getTime(),
            id: generateId(),
            transKeyContent: 'generateCaseworkerDashboardStart_msg1',
            generationId,
            type: 'message',
          },
        },
        {
          generationId,
          status: GenerationStatusEnum.Finished,
          payload: {
            sentAt: new Date().getTime(),
            id: generateId(),
            transKeyContent: 'generateCaseworkerDashboardStart_msg2',
            generationId,
            type: 'message',
          },
        },
      ]
    }

    default:
      return []
  }
}
